import {
  Button,
  DateRangePicker,
  Icon,
  IconSizeClasses,
  VirtualizedTable,
} from '@eppendorf/vnls-react-components';
import { RunRecordReportOutput } from '@eppendorf/vnls-reporting-service-utils';
import { createColumnHelper } from '@tanstack/react-table';
import { type ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, useSearchParams } from 'react-router-dom';

import { isFeatureEnabled } from '$shared/feature-toggle/is-feature-enabled';
import { trackPendoEvent } from '$shared/tracking/trackPendoEvent';

import { DeviceNameCell } from '$components/device/name-cell';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { SearchInput } from '$components/search-input/search-input';
import { CellTimestampWithUser } from '$features/report-hub/components/cell-timestamp-with-user';
import { CopyHashCell } from '$features/report-hub/components/copy-hash-cell';
import { DeleteReportConfirmWrapper } from '$features/report-hub/components/delete-report-confirm-wrapper';
import { ReportListEmptyState } from '$features/report-hub/components/report-list-empty-state';
import { ReportStatusBadge } from '$features/report-hub/components/report-status-badge';
import {
  getRunTypeIcon,
  RunRecordInfoCell,
} from '$features/report-hub/components/run-record-info-cell';
import {
  ReportStatusFilter,
  ReportStatusFilterComponent,
} from '$features/report-hub/components/status-filter';
import {
  useDownloadReport,
  useInfiniteRunRecordReports,
} from '$features/report-hub/report-hub.api';
import {
  formatFileSize,
  isKnownUser,
  REPORT_STATUS_READY,
} from '$features/report-hub/report-hub.utils';
import { getRunRecordOutcomeAppearance } from '$features/report-hub/run-record/run-record-outcome';
import { useReportListFilters } from '$features/report-hub/use-report-list-filters';

import './run-record-list.scss';

function RunOutcomeCell({ outcome }: { outcome: string }): ReactElement {
  const { t } = useTranslation();
  const { icon, colorClassName } = getRunRecordOutcomeAppearance(outcome);
  const translationKey = `reportHub.table.runOutcomes.${outcome}` as const;
  const label = t(translationKey, { defaultValue: outcome });

  return (
    <div className="run-record-outcome flex flex__ai--center gap-s">
      <Icon
        name={icon}
        size={IconSizeClasses.XSmall}
        className={`${colorClassName} flex`}
      />
      <span>{label}</span>
    </div>
  );
}

export function RunRecordList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    apiStatus,
    apiSearchQuery,
    from,
    to,
    statusFilter,
    searchQuery,
    dateRange,
    selectedQuickOption,
    isDeleteReportDialogOpen,
    selectedReport,
    setIsDeleteReportDialogOpen,
    copiedHashReportId,
    handleSearchQueryChange,
    handleDateRangeChange,
    handleQuickSelectOption,
    handleDeleteReport,
    handleCopyHash,
    handleStatusChange,
    resetSearch,
  } = useReportListFilters();

  const handleRowClick = useCallback(
    (report: RunRecordReportOutput) => {
      // Mirrors the sidecar route key in `sidecar-routes.tsx` (`SideCarRoutes.RunRecord`).
      navigate({ search: `?sidecarRoute=run-record/detail&id=${report.id}` });
    },
    [navigate],
  );

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteRunRecordReports(apiSearchQuery, apiStatus, from, to);

  const downloadReportMutation = useDownloadReport();

  const handleDownloadReport = useCallback(
    async (reportId: string) => {
      try {
        const downloadUrl = await downloadReportMutation.mutateAsync(reportId);
        window.open(downloadUrl, '_blank');
      } catch (error) {
        // eslint-disable-next-line no-console -- intentional error logging
        console.error('Failed to download report:', error);
      }
    },
    [downloadReportMutation],
  );

  const reports = data?.pages?.flatMap((page) => page || []) ?? [];
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    statusFilter !== ReportStatusFilter.All ||
    Boolean(dateRange.from) ||
    Boolean(dateRange.to);
  const shouldShowNoDataEmptyState = !hasActiveFilters && reports.length === 0;

  const routeError = useRouteError();

  const isDevtoolingEnabled = isFeatureEnabled('devtooling');

  const columnHelper = useMemo(() => createColumnHelper<RunRecordReportOutput>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('status', {
        header: () => t('shared.status'),
        meta: {
          dataLabel: t('shared.status'),
        },
        id: 'status',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => <ReportStatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('deviceName', {
        header: () => t('reportHub.table.device'),
        meta: {
          dataLabel: t('reportHub.table.device'),
        },
        id: 'deviceName',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          return (
            <DeviceNameCell
              device={{
                name: report.deviceName || undefined,
                manufacturer: report.manufacturer,
                model: report.deviceModel || undefined,
                serialNumber: report.serialNumber,
              }}
            />
          );
        },
      }),
      columnHelper.display({
        header: () => t('reportHub.table.programName'),
        meta: {
          dataLabel: t('reportHub.table.programName'),
        },
        id: 'programName',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          return (
            <RunRecordInfoCell
              iconName={getRunTypeIcon(report.runType)}
              title={report.programName ?? t('reportHub.table.standard')}
              subtitle={report.programInfo ?? ''}
            />
          );
        },
      }),
      columnHelper.accessor('runOutcome', {
        header: () => t('reportHub.table.runOutcome'),
        meta: {
          dataLabel: t('reportHub.table.runOutcome'),
        },
        id: 'runOutcome',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => <RunOutcomeCell outcome={info.getValue()} />,
      }),
      columnHelper.accessor('startDate', {
        header: () => t('reportHub.table.start'),
        meta: {
          dataLabel: t('reportHub.table.start'),
        },
        id: 'start',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          return (
            <CellTimestampWithUser
              timestamp={info.getValue()}
              userLabel={
                isKnownUser(report.user)
                  ? t('reportHub.table.startedBy', { user: report.user })
                  : t('reportHub.table.started')
              }
            />
          );
        },
      }),
      columnHelper.accessor('endDate', {
        header: () => t('reportHub.table.end'),
        meta: {
          dataLabel: t('reportHub.table.end'),
        },
        id: 'end',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          return (
            <CellTimestampWithUser
              timestamp={info.getValue()}
              userLabel={
                isKnownUser(report.endUser)
                  ? t('reportHub.table.endedBy', { user: report.endUser })
                  : t('reportHub.table.ended')
              }
            />
          );
        },
      }),
      columnHelper.accessor('hash', {
        header: () => t('reportHub.table.sha256Hash'),
        meta: {
          dataLabel: t('reportHub.table.sha256Hash'),
        },
        id: 'hash',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const hash = info.getValue();
          const report = info.row.original;
          if (!hash) return '-';

          return (
            <CopyHashCell
              hash={hash}
              reportId={report.id}
              copiedHashReportId={copiedHashReportId}
              onCopy={handleCopyHash}
            />
          );
        },
      }),
      columnHelper.accessor('fileSize', {
        header: () => t('reportHub.table.fileSize'),
        meta: {
          dataLabel: t('reportHub.table.fileSize'),
        },
        id: 'fileSize',
        cell: (info) => formatFileSize(info.getValue()),
      }),
      columnHelper.display({
        header: () => t('reportHub.table.download'),
        meta: {
          dataLabel: t('reportHub.table.download'),
        },
        id: 'download',
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const report = info.row.original;
          if (report.status === REPORT_STATUS_READY) {
            return (
              <Button
                size="small"
                variant="icon"
                onClick={(e) => {
                  trackPendoEvent('download', { reportId: report.id });
                  e.stopPropagation();
                  handleDownloadReport(report.id);
                }}
                aria-label={t('stabilityDetail.actions.downloadReportAriaLabel')}
                disabled={downloadReportMutation.isPending}
              >
                <Icon name="save" size={IconSizeClasses.Small} />
              </Button>
            );
          }
          return <span>-</span>;
        },
      }),
      ...(isDevtoolingEnabled
        ? [
            columnHelper.display({
              header: () => t('shared.actions'),
              meta: {
                dataLabel: t('shared.actions'),
              },
              id: 'actions',
              // eslint-disable-next-line react/no-unstable-nested-components -- accepted
              cell: ({ row }) => (
                <Button
                  onClick={(e) => {
                    handleDeleteReport(e, row.original);
                  }}
                  variant="icon"
                >
                  <Icon name="delete" size={IconSizeClasses.XSmall} />
                </Button>
              ),
            }),
          ]
        : []),
    ],
    [
      columnHelper,
      copiedHashReportId,
      downloadReportMutation.isPending,
      handleCopyHash,
      handleDeleteReport,
      handleDownloadReport,
      isDevtoolingEnabled,
      t,
    ],
  );

  return (
    <>
      <div className="run-record-list__header">
        <ReportStatusFilterComponent
          selectedStatus={statusFilter}
          onStatusChange={handleStatusChange}
          className="report-status-filter-component"
        />

        <DateRangePicker
          dateRange={{
            from: dateRange.from,
            to: dateRange.to,
          }}
          onDateRangeChange={handleDateRangeChange}
          autoFocus
          disabled={{
            after: new Date(),
          }}
          quickSelect={{
            enabled: true,
            onOptionSelect: handleQuickSelectOption,
            selectedOption: selectedQuickOption,
          }}
        />

        <SearchInput
          onSearchQueryChange={handleSearchQueryChange}
          placeholder={t('reportHub.search.placeholder')}
          id="run-record-search"
          value={searchQuery}
          className="run-record-search bg-white"
        />
      </div>

      {(routeError || isError) && <LoadingErrorHint />}

      {!isLoading && !isError && !shouldShowNoDataEmptyState && (
        <div
          className="flex flex__dir--column"
          style={{
            height: reports.length > 0 ? '100vh' : '50vh',
          }}
        >
          <VirtualizedTable
            className="run-record-table"
            data={reports}
            columns={columns}
            onRowClick={handleRowClick}
            isSelectedRow={(report) => searchParams.get('id') === report.id}
            loadMore={() => {
              if (hasNextPage && !isFetching) {
                fetchNextPage();
              }
            }}
            isLoading={isFetching}
            onResetSearch={resetSearch}
            isTableToCardDesign
          />
        </div>
      )}

      {!isLoading && !isError && shouldShowNoDataEmptyState && (
        <ReportListEmptyState
          title={t('reportHub.emptyState.runRecords.title')}
          description={t('reportHub.emptyState.runRecords.description')}
          actionLabel={t('reportHub.emptyState.runRecords.action')}
          onActionClick={() => navigate('/devices')}
        />
      )}
      <DeleteReportConfirmWrapper
        isDeleteConfirmOpen={isDeleteReportDialogOpen}
        onOpenChange={setIsDeleteReportDialogOpen}
        selectedReport={selectedReport}
      />
    </>
  );
}
